import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { Save, Send } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import {
  ROLE_LABELS,
  roleSatisfies,
  type AdminApi,
  type AdminLegalDocument,
  type AdminLegalDocumentType,
  type AdminLegalVersion,
  type AdminRole,
  type SensitiveActionInput,
} from '@/admin/adminApi';
import { formatCompactInstant } from '@/admin/data/payload';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { SensitiveActionDialog } from '@/admin/SensitiveActionDialog';
import { SENSITIVE_ACTIONS } from '@/admin/sensitiveActions';
import { adminErrorCode } from '@/admin/settings/adminErrorMessage';
import { httpStatusOf, messageOf } from '@/admin/users/adminErrors';
import { AccessDenied, Badge, Button, Card, ErrorState, Field, LoadingState } from '@/ui/kit';

import {
  LEGAL_DOCUMENT_ACRONYMS,
  LEGAL_DOCUMENT_TITLES,
  MAX_CONTENT_LENGTH,
  actorLabel,
  contentLength,
  serverTrim,
} from './legalDocument';
import { MobilePreview } from './MobilePreview';
import { VersionHistory } from './VersionHistory';

/**
 * Un document legal : editeur, historique, apercu et publication (maquette P18).
 *
 * Sept regles, chacune testee :
 *
 * 1. **L'editeur part de ce que les parents lisent, ou du brouillon.** Sans
 *    brouillon, il s'ouvre sur la version publiee et dit « Aucune modification en
 *    cours » ; le premier enregistrement cree le brouillon.
 * 2. **Enregistrer est un geste** (aucune sauvegarde automatique : chaque
 *    enregistrement est une ligne d'audit). L'horodatage affiche est celui de la
 *    **reponse** du serveur, jamais celui du navigateur.
 * 3. **Publier ce qui est enregistre, pas ce qui est tape.** Le bouton reste
 *    inactif tant que le texte differe du brouillon : publier un texte que le
 *    serveur n'a pas, c'est publier autre chose que ce qui sera trace.
 * 4. **Un 409 ne perd jamais de texte.** Quelqu'un d'autre a enregistre ou publie
 *    pendant la frappe : rien n'est ecrit, le texte reste dans l'editeur, et
 *    l'ecran montre le brouillon concurrent avec deux choix explicites — le
 *    recharger (remplace la saisie, a la demande), ou garder la sienne (le
 *    prochain enregistrement remplacera celui de l'autre, en connaissance de cause).
 * 5. **Ce que fait la publication est ecrit avant le geste** : la boite dit que
 *    les parents verront la version tout de suite et que l'ancienne reste
 *    consultable.
 * 6. **`support` lit, ne modifie pas** : le texte est affiche, aucun bouton
 *    d'ecriture n'existe, et l'ecran dit pourquoi. Le serveur reste l'autorite.
 * 7. **Consulter une version ne touche pas a l'editeur** : il reste monte, avec
 *    sa saisie, pendant qu'une version publiee s'affiche en lecture seule.
 */

const publishPolicy = SENSITIVE_ACTIONS.publish_legal_document;

const describeFailure = (error: unknown): { readonly message: string; readonly code: string | null } => ({
  message: messageOf(error),
  code: adminErrorCode(error),
});

/** Texte en lecture seule : focalisable, pour qu'un long texte se parcoure au clavier. */
const ReadOnlyText = ({ label, content }: { readonly label: string; readonly content: string }): ReactElement => (
  <Box
    role="region"
    aria-label={label}
    // Focalisable : un texte long doit se parcourir au clavier.
    tabIndex={0}
    sx={{
      p: 1.75,
      maxHeight: 420,
      overflowY: 'auto',
      borderRadius: '12px',
      border: 1,
      borderColor: 'tokens.border',
      bgcolor: 'tokens.surfaceMuted',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      fontSize: 13,
      lineHeight: '20px',
      color: 'tokens.textStrong',
      '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2 },
    }}
  >
    {content}
  </Box>
);

interface Conflict {
  /** Le brouillon courant du serveur, `null` s'il a ete publie ou n'existe plus. */
  readonly draft: AdminLegalDocument['draft'];
  readonly published: AdminLegalDocument['published'];
}

const DocumentEditor = ({
  api,
  type,
  doc,
  initial,
  role,
  operator,
  token,
  onChanged,
}: {
  readonly api: AdminApi;
  readonly type: AdminLegalDocumentType;
  readonly doc: AdminLegalDocument | undefined;
  readonly initial: AdminLegalVersion;
  readonly role: AdminRole;
  readonly operator: { readonly id: string; readonly email: string };
  readonly token: number;
  readonly onChanged: () => void;
}): ReactElement => {
  const canWrite = roleSatisfies(role, 'admin');
  const [stored, setStored] = useState<AdminLegalVersion>(initial);
  const [text, setText] = useState(initial.content);
  /** Revision envoyee a l'enregistrement : `0` quand aucun brouillon n'existe. */
  const [baseRevision, setBaseRevision] = useState(initial.status === 'draft' ? initial.revision : 0);
  const [viewing, setViewing] = useState<number | null>(null);
  const [viewAttempt, setViewAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [dialogError, setDialogError] = useState<{ readonly message: string; readonly code: string | null } | null>(null);

  const viewed = useAdminQuery(
    `legal-view|${type}|${String(viewing)}`,
    () => api.getLegalVersion(type, viewing ?? 0),
    { enabled: viewing !== null, token: viewAttempt },
  );

  const length = contentLength(text);
  const dirty = serverTrim(text) !== stored.content;
  const tooLong = length > MAX_CONTENT_LENGTH;
  const empty = length === 0;
  const textError = tooLong
    ? `Le texte dépasse ${MAX_CONTENT_LENGTH.toLocaleString('fr-FR')} caractères (${length.toLocaleString('fr-FR')}).`
    : empty && dirty
      ? 'Le texte ne peut pas être vide.'
      : undefined;
  const hasDraft = stored.status === 'draft';
  /** Revision du brouillon que l'ecran a lue ; `0` quand le texte affiche est la version publiee. */
  const storedRevision = hasDraft ? stored.revision : 0;
  /**
   * Apres « Garder mon texte », la revision annoncee au serveur n'est plus celle
   * du texte affiche : enregistrer est alors necessaire **meme sans modifier le
   * texte** (c'est le geste promis par la carte de conflit), et publier ne l'est
   * pas — il renverrait la revision perimee et boucherait sur un nouveau 409.
   */
  const rebased = baseRevision !== storedRevision;
  const needsSave = dirty || rebased;
  const canSave = canWrite && needsSave && !tooLong && !empty && !saving;
  const canPublish = canWrite && hasDraft && !needsSave && !saving;
  const nextNumber = doc?.nextNumber;
  const publishLabel = nextNumber === undefined ? 'Publier une nouvelle version' : `Publier la version ${String(nextNumber)}`;
  const acronym = LEGAL_DOCUMENT_ACRONYMS[type];
  const otherAuthor = hasDraft && stored.author !== null && stored.author.id !== operator.id ? stored.author : null;

  /** Relit l'etat du serveur apres un refus de revision, et l'expose. */
  const explainConflict = async (): Promise<void> => {
    try {
      const fresh = (await api.listLegalDocuments()).find((candidate) => candidate.type === type);

      setConflict({ draft: fresh?.draft ?? null, published: fresh?.published ?? null });
      onChanged();
    } catch (failure: unknown) {
      setError(messageOf(failure));
    }
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setNotice(null);
    setError(null);

    try {
      const saved = await api.saveLegalDraft(type, text, baseRevision);

      setStored(saved);
      setBaseRevision(saved.revision);
      setConflict(null);
      // L'instant vient de la reponse du serveur, pas de l'horloge du navigateur.
      setNotice(`Brouillon enregistré (révision ${String(saved.revision)}), ${formatCompactInstant(saved.savedAt)}.`);
      onChanged();
    } catch (failure: unknown) {
      if (httpStatusOf(failure) === 409) {
        await explainConflict();
      } else {
        setError(messageOf(failure));
      }
    } finally {
      setSaving(false);
    }
  };

  const reloadFromServer = async (): Promise<void> => {
    if (conflict === null) {
      return;
    }

    setError(null);

    try {
      const version =
        conflict.draft !== null
          ? await api.getLegalVersion(type, 'draft')
          : conflict.published?.number != null
            ? await api.getLegalVersion(type, conflict.published.number)
            : null;

      if (version === null) {
        setError('Aucune version à recharger : le document n’a pas de version publiée.');

        return;
      }

      setStored(version);
      setText(version.content);
      setBaseRevision(version.status === 'draft' ? version.revision : 0);
      setConflict(null);
      setNotice(null);
    } catch (failure: unknown) {
      setError(messageOf(failure));
    }
  };

  const keepMine = (): void => {
    // Le prochain enregistrement remplacera le brouillon de l'autre : c'est la
    // revision courante du serveur qui est annoncee, pas une revision perimee.
    setBaseRevision(conflict?.draft?.revision ?? 0);
    setConflict(null);
  };

  const openDialog = (): void => {
    setDialogError(null);
    setNotice(null);
    setDialogOpen(true);
  };

  const closeDialog = (): void => {
    if (!pending) {
      setDialogOpen(false);
      setDialogError(null);
    }
  };

  const publish = async (input: SensitiveActionInput): Promise<void> => {
    setPending(true);
    setDialogError(null);

    try {
      const published = await api.publishLegalDraft(type, stored.revision, input);

      setDialogOpen(false);
      setStored(published);
      setText(published.content);
      setBaseRevision(0);
      setViewing(null);
      setConflict(null);
      setError(null);
      setNotice(`Version ${String(published.number)} publiée : les parents la voient désormais. L’ancienne reste consultable dans l’historique.`);
      onChanged();
    } catch (failure: unknown) {
      const status = httpStatusOf(failure);

      if (status === 409 || adminErrorCode(failure) === 'no_draft') {
        // Le brouillon a change ou a ete publie ailleurs : la boite n'a plus d'objet.
        setDialogOpen(false);
        await explainConflict();
      } else {
        setDialogError(describeFailure(failure));
      }
    } finally {
      setPending(false);
    }
  };

  const shown = viewing !== null && viewed.data !== null ? viewed.data : null;
  const previewContent = viewing === null ? text : (shown?.content ?? '');

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        alignItems: 'start',
        // L'apercu se pose a droite des 1400 px (maquette 1440) ; en dessous il passe sous l'historique.
        gridTemplateColumns: 'minmax(0, 1fr)',
        '@media (min-width: 1400px)': { gridTemplateColumns: 'minmax(0, 1fr) 300px' },
      }}
    >
      <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        {conflict === null ? null : (
          <Alert
            severity="warning"
            variant="outlined"
            role="alert"
            action={
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="S" onClick={() => void reloadFromServer()}>
                  {conflict.draft === null ? 'Recharger la version publiée' : 'Recharger son brouillon'}
                </Button>
                <Button size="S" onClick={keepMine}>
                  Garder mon texte
                </Button>
              </Box>
            }
          >
            <AlertTitle>Le brouillon a changé pendant que vous écriviez</AlertTitle>
            {conflict.draft === null
              ? 'Le brouillon a été publié ou n’existe plus. '
              : `${actorLabel(conflict.draft.author)} a enregistré la révision ${String(conflict.draft.revision)} (${formatCompactInstant(conflict.draft.savedAt)}). `}
            Rien n’a été écrit et votre texte est conservé ci-dessous.{' '}
            {conflict.draft === null
              ? 'Recharger remplace votre saisie par la version publiée ; garder votre texte vous permet de l’enregistrer comme nouveau brouillon.'
              : 'Recharger remplace votre saisie ; garder votre texte le fera remplacer le brouillon actuel à votre prochain enregistrement.'}
          </Alert>
        )}

        {notice === null ? null : (
          <Alert severity="success" variant="outlined" role="status" onClose={() => { setNotice(null); }}>
            {notice}
          </Alert>
        )}
        {error === null ? null : (
          <Alert severity="error" variant="outlined" role="alert" onClose={() => { setError(null); }}>
            {error}
          </Alert>
        )}

        {/* L'editeur reste monte pendant qu'une version s'affiche : sa saisie ne se perd pas. */}
        <Box hidden={viewing !== null} sx={{ display: viewing === null ? 'block' : 'none' }}>
          <Card
            title={LEGAL_DOCUMENT_TITLES[type]}
            subtitle={
              hasDraft
                ? `Brouillon, révision ${String(stored.revision)} · écrit par ${actorLabel(stored.author)} · enregistré ${formatCompactInstant(stored.savedAt)}`
                : `Version ${String(stored.number)} publiée ${formatCompactInstant(stored.publishedAt)} · Aucune modification en cours`
            }
            actions={
              <>
                {stored.provisional ? <Badge label="Provisoire" tone="warning" /> : null}
                <Badge label={hasDraft ? 'Brouillon' : 'Aucune modification en cours'} dot={hasDraft} />
              </>
            }
          >
            {canWrite ? null : (
              <Alert severity="info" variant="outlined" role="note">
                Votre rôle ({ROLE_LABELS[role]}) permet de lire ce texte, pas de le modifier ni de le publier : ces gestes sont
                réservés au rôle Administrateur.
              </Alert>
            )}
            {otherAuthor === null || !canWrite ? null : (
              <Alert severity="info" variant="outlined" role="note">
                Ce brouillon a été écrit par {actorLabel(otherAuthor)}. L’enregistrer le remplace pour tout le monde.
              </Alert>
            )}
            {canWrite ? (
              <Field
                label={`Texte des ${acronym}`}
                multiline
                minRows={14}
                value={text}
                onChange={(next) => {
                  setText(next);
                  setNotice(null);
                }}
                error={textError}
                hint={
                  <>
                    Texte brut : ni Markdown ni mise en forme, le mobile affiche le texte tel quel.{' '}
                    <Box component="span" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {`${length.toLocaleString('fr-FR')} / ${MAX_CONTENT_LENGTH.toLocaleString('fr-FR')}`}
                    </Box>
                  </>
                }
                disabled={saving}
              />
            ) : (
              <ReadOnlyText label={`Texte des ${acronym}`} content={stored.content} />
            )}
            {canWrite ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Typography sx={{ flex: '1 1 200px', fontSize: 12, color: 'tokens.textMuted' }}>
                  {needsSave
                    ? 'Modifications non enregistrées : enregistrez le brouillon avant de le publier.'
                    : hasDraft
                      ? 'Le brouillon est enregistré. Il n’est pas encore lu par les parents.'
                      : 'Aucune modification en cours : les parents lisent cette version.'}
                </Typography>
                <Button icon={Save} disabled={!canSave} onClick={() => void save()}>
                  {saving ? 'Enregistrement…' : 'Enregistrer le brouillon'}
                </Button>
                <Button variant="primary" icon={Send} disabled={!canPublish} onClick={openDialog}>
                  {publishLabel}
                </Button>
              </Box>
            ) : null}
          </Card>
        </Box>

        {viewing === null ? null : (
          <Card
            title={`Version ${String(viewing)} — lecture seule`}
            subtitle={
              shown === null
                ? undefined
                : `Publiée ${formatCompactInstant(shown.publishedAt)} par ${actorLabel(shown.publishedBy)} · ${shown.length.toLocaleString('fr-FR')} caractères`
            }
            actions={
              <Button
                onClick={() => {
                  setViewing(null);
                }}
              >
                Revenir à l’éditeur
              </Button>
            }
          >
            {viewed.failure?.forbidden === true ? (
              <AccessDenied title="Votre rôle ne permet pas de consulter cette version">
                Votre compte reste connecté : il manque le rôle d’administration requis (au moins « Support »).
              </AccessDenied>
            ) : null}
            {viewed.failure !== null && !viewed.failure.forbidden ? (
              <ErrorState
                message={viewed.failure.message}
                onRetry={() => {
                  setViewAttempt((current) => current + 1);
                }}
              />
            ) : null}
            {shown === null && viewed.failure === null ? <LoadingState label="Chargement de la version…" /> : null}
            {shown === null ? null : (
              <>
                <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
                  Une version publiée ne se modifie plus : pour la corriger, on en publie une autre.
                </Typography>
                <ReadOnlyText label={`Texte de la version ${String(viewing)}`} content={shown.content} />
              </>
            )}
          </Card>
        )}

      </Box>

      <MobilePreview type={type} content={previewContent} />

      {/* Pleine largeur, sous l'editeur et l'apercu : un tableau de cinq colonnes n'a pas sa place dans la colonne de l'editeur. */}
      <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
        <VersionHistory
          api={api}
          type={type}
          token={token}
          viewing={viewing}
          onView={(number) => {
            setViewing(number);
          }}
        />
      </Box>

      {dialogOpen ? (
        <SensitiveActionDialog
          open
          title={publishLabel}
          subtitle={`${LEGAL_DOCUMENT_TITLES[type]} · révision ${String(stored.revision)} du brouillon`}
          consequence={{
            summary: 'Les parents verront immédiatement cette version. L’ancienne reste consultable.',
            disappears: [],
            kept: [
              'L’ancienne version reste consultable dans l’historique.',
              `La version ${nextNumber === undefined ? 'publiée' : String(nextNumber)} ne pourra plus être modifiée : pour la corriger, on en publiera une autre.`,
              'Le brouillon disparaît : son texte devient la version publiée.',
            ],
            keptTitle: 'Ce que fait la publication',
          }}
          reasonOptions={publishPolicy.reasons}
          requiresPassword={publishPolicy.requiresPassword}
          operatorEmail={operator.email}
          expectedConfirmation={type}
          confirmationLabel="le type du document"
          actionLabel={publishLabel}
          pending={pending}
          error={dialogError?.message ?? null}
          errorCode={dialogError?.code ?? null}
          onCancel={closeDialog}
          onConfirm={(input) => void publish(input)}
        />
      ) : null}
    </Box>
  );
};

/**
 * Charge le texte de depart d'un document — le brouillon s'il existe, sinon la
 * version publiee — puis monte l'editeur, qui en est ensuite proprietaire.
 *
 * Le chargement n'est fait **qu'une fois** par ouverture (ou par « Reessayer ») :
 * les relectures de l'ecran (« Actualiser ») rafraichissent la liste et
 * l'historique, jamais la saisie en cours.
 */
export const DocumentPanel = ({
  api,
  type,
  doc,
  role,
  operator,
  token,
  onChanged,
}: {
  readonly api: AdminApi;
  readonly type: AdminLegalDocumentType;
  readonly doc: AdminLegalDocument | undefined;
  readonly role: AdminRole;
  readonly operator: { readonly id: string; readonly email: string };
  readonly token: number;
  readonly onChanged: () => void;
}): ReactElement => {
  const [attempt, setAttempt] = useState(0);
  const initial = useAdminQuery(`legal-initial|${type}|${String(attempt)}`, async (): Promise<AdminLegalVersion> => {
    if (doc?.draft != null) {
      return api.getLegalVersion(type, 'draft');
    }

    if (doc?.published?.number != null) {
      return api.getLegalVersion(type, doc.published.number);
    }

    throw new Error('Ce document n’a ni brouillon ni version publiée.');
  });

  if (initial.failure?.forbidden === true) {
    return (
      <AccessDenied title="Votre rôle ne permet pas de consulter ce document">
        Votre compte reste connecté : il manque le rôle d’administration requis (au moins « Support »).
      </AccessDenied>
    );
  }

  if (initial.failure !== null) {
    return (
      <ErrorState
        message={initial.failure.message}
        onRetry={() => {
          setAttempt((current) => current + 1);
        }}
      />
    );
  }

  if (initial.data === null) {
    return <LoadingState label="Chargement du document…" />;
  }

  return (
    <DocumentEditor
      key={`${type}|${String(attempt)}`}
      api={api}
      type={type}
      doc={doc}
      initial={initial.data}
      role={role}
      operator={operator}
      token={token}
      onChanged={onChanged}
    />
  );
};
